/**
 * CreditOS v2 — Tipos centrais do sistema
 * Baseados no Brief v4.0 e modelo de dados Supabase
 * Convencao: todas as tabelas tem prefixo credit_v2_
 */

// ========================================================
// ROLES E AUTENTICACAO
// ========================================================

export type AppRole =
  | "super_admin"
  | "network_manager"
  | "tenant_admin"
  | "backoffice"
  | "seller"
  | "ad_agency"

export const ROLE_HIERARCHY: Record<AppRole, number> = {
  super_admin: 100,
  network_manager: 80,
  tenant_admin: 60,
  backoffice: 40,
  seller: 20,
  ad_agency: 10,
}

export const ROLES_WITH_INVITE_PERMISSION: AppRole[] = [
  "super_admin",
  "network_manager",
  "tenant_admin",
]

/**
 * Roles oferecidas como opção na UI de convite/edição de membros.
 * super_admin nunca é convidável (segurança). network_manager e ad_agency
 * continuam funcionando no sistema (PERMISSION_MAP intacto e usuários
 * existentes preservados), mas seguem fora do fluxo de criação por enquanto.
 */
export const VISIBLE_ROLES_FOR_INVITE: AppRole[] = [
  "tenant_admin",
  "backoffice",
  "seller",
]

export const ROLES_WITH_FUNIL_ACCESS: AppRole[] = [
  "super_admin",
  "network_manager",
  "tenant_admin",
  "backoffice",
  "seller",
]

// ========================================================
// PERFIL E USUARIO
// ========================================================

export interface UserPreferences {
  /** Navegar entre abas do card de lead com scroll da roda do mouse. */
  wheelTabNavigation?: boolean
}

export interface Profile {
  id: string
  full_name: string | null
  avatar_url: string | null
  email?: string | null
  preferences?: UserPreferences | null
  created_at: string
  slug?: string | null
}

// ========================================================
// LANDING PAGES
// ========================================================

export type LandingPageTipo = 'unidade' | 'vendedor'

export interface LandingPage {
  id: string
  tenant_id: string
  seller_id: string | null
  tipo: LandingPageTipo
  is_active: boolean
  headline_override: string | null
  subtitle_override: string | null
  valor_min: number
  valor_max: number
  created_by: string | null
  created_at: string
  updated_at: string
}

export interface UserRole {
  user_id: string
  role: AppRole
  created_at: string
}

export interface UserWithProfile {
  id: string
  email: string
  profile: Profile | null
  role: AppRole
}

// ========================================================
// TENANTS (ESCs)
// ========================================================

export interface CreditAnalysisRules {
  cnae_genericos: string[]
  tolerancias?: Record<string, number>
  pesos_ive?: Record<string, number>
}

export interface DocumentRequirements {
  rg_cnh: boolean
  selfie: boolean
  comprovante_endereco: boolean
  comprovante_endereco_residencial: boolean
  comprovante_endereco_comercial: boolean
  foto_externa: boolean
  video_interno: boolean
}

export interface KYCConfig {
  analise_video_habilitada: boolean
  analise_selfie_habilitada: boolean
  analise_redes_sociais_habilitada: boolean
  persona_gemini?: string
  // #198: destino configurável dos leads fora da área (roteamento automático
  // do #191). Guarda o id de uma coluna TERMINAL do kanban do tenant. Ausente
  // ou null = comportamento padrão (leads fora da área caem em "Reprovado").
  // Mora aqui (jsonb já persistido por ConfigESC) para evitar migration.
  out_of_area_column_id?: string | null
}

export interface AIConfig {
  persona?: string
  segmentos?: string[]
  tom?: string
}

export interface EmailNotificationConfig {
  novo_lead: boolean
  lead_movido: boolean
  ive_calculado: boolean
  convite_usuario: boolean
  // Faturamento (opcionais — ausente = desligado)
  pagamento_confirmado?: boolean
  pagamento_atrasado?: boolean
  creditos_baixos?: boolean
  creditos_devolvidos?: boolean
}

export interface LoanPlan {
  valor: number
  parcelas: number
  valor_parcela: number
}

export interface InstallmentConfig {
  enabled: boolean
  show_valor_parcela: boolean
  plans: LoanPlan[]
  panfleto_url?: string | null
  /** Destaque opcional pra uma das opções de valor na LP — task #13.27.
   *  Substitui o antigo "Popular" hardcoded no meio. Admin define em
   *  Configurações > Produto qual valor recebe a tag e qual o texto.
   *  Quando undefined/null: fallback pro comportamento legado ("Popular"
   *  na opção do meio). Quando `enabled=false`: nenhuma tag aparece. */
  highlight?: {
    enabled: boolean
    label: string
    valor: number
  } | null
}

export interface Tenant {
  id: string
  slug: string
  name: string
  logo_url: string | null
  primary_color: string
  allowed_cities: string[]
  credit_analysis_rules: CreditAnalysisRules
  document_requirements: DocumentRequirements
  kyc_config: KYCConfig
  ai_config: AIConfig
  email_notifications: EmailNotificationConfig
  created_at: string
  // Campos adicionais do cadastro "mágico" de ESC
  cnpj?: string
  razao_social?: string
  nome_fantasia?: string
  endereco?: TenantEndereco
  email_contato?: string
  gestor_user_id?: string
  // WhatsApp de contato (link wa.me da landing page)
  contact_whatsapp?: string | null
  // Auto-assign round-robin
  auto_assign?: boolean
  round_robin_sellers?: string[]
  // Seller restricted view
  seller_restricted_view?: boolean
  // #191: reprovação automática de leads fora da área de atendimento (opt-in,
  // default false). Quando true, leads novos de canais automáticos (LP, Meta,
  // SDR) com cidade fora de allowed_cities nascem na coluna "Reprovado".
  auto_reject_out_of_area?: boolean
  // Installment config (loan plans from pamphlet)
  installment_config?: InstallmentConfig | null
  // IVE: v3 é o motor universal (cutover 2026-06-11). O backend força v3 oficial
  // independente deste flag; mantido por compatibilidade. Default do banco: '3.0'.
  ive_version_ativa?: "2.0" | "3.0-shadow" | "3.0"
  // #10.4 Fase 2B: Meta Pixel ID injetado nas LPs (15-16 dígitos). NULL = sem pixel.
  meta_pixel_id?: string | null
  // #10.4 Fase 2C: Conversions API access token + toggle.
  meta_conversions_access_token?: string | null
  conversions_api_enabled?: boolean
  // #222: soft-delete (desativacao reversivel)
  deactivated_at?: string | null
  deactivated_by?: string | null
  // Migration 293: unidade que a pessoa lê sem ter vínculo (leitura da rede).
  // Só existe no navegador, posta por `juntarUnidadesLidas`; a gravação lá
  // continua recusada pela RLS.
  somente_leitura?: boolean
}

export interface TenantEndereco {
  logradouro: string
  numero: string
  complemento: string
  bairro: string
  cidade: string
  uf: string
  cep: string
}

// ========================================================
// CIDADES (IBGE)
// ========================================================

export interface CityWithIBGE {
  nome: string
  estado: string
  ibge: string
  isSede?: boolean
}

/**
 * Normaliza lista de cidades para o formato CityWithIBGE.
 * Aceita tanto string[] (formato legado) quanto CityWithIBGE[].
 */
export function normalizeCityList(cities: (string | CityWithIBGE)[]): CityWithIBGE[] {
  return cities.map((city) => {
    if (typeof city === "string") {
      return { nome: city, estado: "", ibge: "" }
    }
    return city
  })
}

// ========================================================
// ESC COMPANY DATA (Enriquecimento BigDataCorp/Gemini)
// ========================================================

export interface ESCCompanyData {
  cnpj: string
  razaoSocial: string
  nomeFantasia: string
  cnae: string
  cnaeDescricao: string
  /** `null` quando o biro nao achou a empresa. Nao existe valor padrao aqui:
   *  este campo ja teve "ATIVA" como padrao e afirmava empresa ativa para
   *  documento inexistente (relato MABAL ESC ITU, 20/08/2026). */
  situacao: SituacaoCNPJ | null
  dataAbertura: string
  capitalSocial: number
  endereco: {
    logradouro: string
    numero: string
    complemento: string
    bairro: string
    cidade: string
    uf: string
    cep: string
  }
  emailContato: string
  socios: Array<{ nome: string; cpf: string; qualificacao: string }>
  regimeTributario?: string | null
}

export interface CNPJCardExtractionResult {
  cnpj: string | null
  razaoSocial: string | null
  confidence: number
  rawText: string
}

// ========================================================
// WIZARD CRIAR ESC
// ========================================================

export interface CreateESCWizardData {
  // Step 1: Empresa
  company: ESCCompanyData
  cnpjCardFile?: File | null
  extractionMethod: "upload" | "manual"
  // Step 2: Cidades
  cidadesAtendidas: CityWithIBGE[]
  // Step 3: Gestor
  gestorEmail: string
  gestorIsExistingUser: boolean
  // Step 4: Identidade (opcional)
  displayName?: string
  primaryColor?: string
  logoFile?: File | null
  panfletoFile?: File | null
}

export interface SellerConfig {
  cidades_atendidas?: string[]
  limite_leads?: number
}

export interface UserTenant {
  id: string
  user_id: string
  tenant_id: string
  seller_config: SellerConfig
  created_at: string
  tenant?: Tenant
}

// ========================================================
// KANBAN
// ========================================================

export type KanbanStatusOrder = -1 | 0 | 1 | 2 | 3 | 4

export type DefaultColumnSlug =
  | "novo_lead"
  | "cadastro_completo"
  | "analisado_ive"
  | "necessita_visita"
  | "emitir_contrato"
  | "assinatura_contrato"
  | "venda_realizada"
  | "sem_interesse"
  | "reprovado"

export interface KanbanColumn {
  id: string
  tenant_id: string
  slug: DefaultColumnSlug | null
  name: string
  color: string
  status_order: KanbanStatusOrder
  position: number
  is_default: boolean
  is_terminal: boolean
  /** A unidade declarou "quem chega aqui já é cliente" (migration 179). O
   *  agente de atendimento lê isto para reconhecer quem volta a escrever sem
   *  perguntar nome e CNPJ de novo. Nasce `false` em toda etapa. */
  is_client_stage: boolean
  created_at: string
}

// ========================================================
// LEADS
// ========================================================

/**
 * Classificação final do IVE.
 * - BAIXO_RISCO / MEDIO_RISCO / ALTO_RISCO: score calculado dentro das faixas.
 * - IMPEDIDO: existe ao menos um impedimento explícito (IMP-XX). Score = 0.
 * - INFORMACAO_FALTANTE: lead sem CPF e sem CNPJ — IVE não roda, UI mostra alerta.
 *   Score = 0 mas semanticamente distinto de IMPEDIDO.
 */
export type IVEClassification =
  | "BAIXO_RISCO"
  | "MEDIO_RISCO"
  | "ALTO_RISCO"
  | "IMPEDIDO"
  | "INFORMACAO_FALTANTE"

export type LeadOrigem = "whatsapp" | "landing_page" | "manual" | "meta_ads" | "google_ads" | "indicacao" | "vendedor" | "soma"

export type SituacaoCNPJ = "ATIVA" | "BAIXADA" | "INAPTA" | "SUSPENSA"

export interface QualifiedContact {
  valor: string
  principal?: boolean
  tipo?: string
}

export interface Socio {
  nome: string
  cpf: string | null
  participacao: number
  qualificacao: string
  data_entrada: string | null
  // Enriched per-partner data
  telefones?: string[]
  emails?: string[]
  cidade?: string | null
  uf?: string | null

  // Processos judiciais do sócio. `undefined` = não consultado (MEI, sócio sem
  // CPF, consulta falhou); `false` = consultado e sem processo. A tela precisa
  // saber a diferença — "não sei" não pode virar um selo de "está limpo".
  tem_processos_reu?: boolean
  tem_processos_requerido?: boolean
  qtd_processos?: number
  /** Quando alguém pagou para consultar este sócio. Ausente = nunca consultado. */
  consultado_em?: string | null
}

export interface EnderecoValidado {
  logradouro: string
  numero: string | null
  complemento: string | null
  bairro: string
  cidade: string
  estado: string
  cep: string
  latitude: number
  longitude: number
  place_id: string | null
  street_view_url: string | null
}

export interface ProcessoJudicial {
  numero: string
  tipo: string
  polo?: string
  descricao: string
  tribunal: string
  data: string | null
  dataDistribuicao?: string | null
  valor?: number | null
  assunto?: string
  vara?: string
  comarca?: string
  juiz?: string
  status?: string
  transitoJulgado?: string | null
  dataEncerramento?: string | null
}

export interface IVEImpedimento {
  codigo: string
  descricao: string
}

export interface IVEFator {
  fator: string
  pontos: number
  descricao?: string
}

export interface WebhookEtapa1 {
  visitorId: string
  sessionId: string
  timestamp: string
  etapa: 1
  nome_completo: string
  cidade: string
  estado: string
  celular: string
  possui_cnpj: boolean
  possui_fachada: boolean
  valor_desejado: number
  parcela: string
  latitude?: number
  longitude?: number
  origem: LeadOrigem
}

export interface WebhookEtapa2 {
  visitorId: string
  sessionId: string
  timestamp: string
  etapa: 2
  celular: string
  cpf: string
  cnpj: string
  email: string
  cidade: string
}

export interface WebhookEtapa3 {
  visitorId: string
  sessionId: string
  timestamp: string
  etapa: 3
  cpf: string
  cnpj: string
  celular: string
  cidade: string
  nome_familiar: string
  telefone_familiar: string
  vendedor?: string
  link_rg_cnh: string
  link_selfie_documento: string
  link_comprovante_endereco: string
  link_foto_externa: string
  link_foto_interna: string
}

export type WebhookPayload = WebhookEtapa1 | WebhookEtapa2 | WebhookEtapa3

// ── Document Types (platform extracts) ────────────────────────────────────

export type DocType = "extrato_bancario" | "ifood" | "uber" | "mercado_livre" | "outras_fontes"

export const DOC_TYPE_LABELS: Record<DocType, string> = {
  extrato_bancario: "Extrato bancário",
  ifood: "Extrato iFood",
  uber: "Extrato Uber",
  mercado_livre: "Extrato Mercado Livre / Mercado Pago",
  outras_fontes: "Outras Fontes de Renda",
}

export const DOC_TYPE_ICONS: Record<DocType, string> = {
  extrato_bancario: "Landmark",
  ifood: "Bike",
  uber: "Car",
  mercado_livre: "ShoppingBag",
  outras_fontes: "Landmark",
}

export const DOC_TYPE_HINTS: Record<DocType, string> = {
  extrato_bancario: "PDF do extrato bancário completo",
  ifood: "Captura de tela do app ou PDF do portal do entregador",
  uber: "Resumo fiscal ou extrato mensal do app Uber",
  mercado_livre: "Relatório de faturamento ou extrato Mercado Pago",
  outras_fontes: "Comprovante de outras fontes de renda (ex: aluguel, freelance)",
}

// ── All Document Types (personal + platform) ─────────────────────────────

export type PersonalDocType = "rg_cnh" | "selfie_documento" | "comprovante_endereco" |
  "comprovante_endereco_residencial" | "comprovante_endereco_comercial" |
  "foto_externa" | "foto_interna" | "video_apresentacao"

// Task #150: "outros" pra documentos que não se encaixam nas categorias
// padrão (ex: contrato de aluguel, declaração de IR, certidões, etc.).
// Backoffice analisa manualmente; IA pode fazer triagem proativa em v2.
// Task wdwxndxp5n: "documentos_avalista" pra os papeis de quem avaliza o
// cliente (RG/CNH, comprovante de endereco, comprovante de renda do avalista).
// Deliberadamente SEM coluna legacy em credit_v2_leads: e isso que garante que
// o documento do avalista nunca sobrescreve link_rg_cnh nem entra na analise de
// IA, que compara nome e CPF do documento com os do SOLICITANTE.
export type AllDocType = DocType | PersonalDocType | "redes_sociais" | "documentos_avalista" | "outros"

export const ALL_DOC_TYPE_LABELS: Record<AllDocType, string> = {
  ...DOC_TYPE_LABELS,
  rg_cnh: "RG / CNH",
  selfie_documento: "Selfie com Documento",
  comprovante_endereco: "Comprovante de Endereço",
  comprovante_endereco_residencial: "Endereço Residencial",
  comprovante_endereco_comercial: "Endereço Comercial",
  foto_externa: "Foto Fachada (Externa)",
  foto_interna: "Foto Interna",
  video_apresentacao: "Vídeo de Apresentação",
  redes_sociais: "Redes Sociais (Screenshots)",
  documentos_avalista: "Documentos do avalista",
  outros: "Outros documentos",
}

export const ALL_DOC_TYPE_ICONS: Record<AllDocType, string> = {
  ...DOC_TYPE_ICONS,
  rg_cnh: "CreditCard",
  selfie_documento: "Camera",
  comprovante_endereco: "MapPin",
  comprovante_endereco_residencial: "Home",
  comprovante_endereco_comercial: "Building2",
  foto_externa: "Building2",
  foto_interna: "Home",
  video_apresentacao: "Video",
  redes_sociais: "Globe",
  documentos_avalista: "UserCheck",
  outros: "FileText",
}

export const ALL_DOC_TYPE_HINTS: Record<AllDocType, string> = {
  ...DOC_TYPE_HINTS,
  rg_cnh: "Foto do RG ou CNH (frente e verso)",
  selfie_documento: "Foto segurando o documento ao lado do rosto",
  comprovante_endereco: "Conta de luz, água ou telefone recente",
  comprovante_endereco_residencial: "Conta de luz, água ou telefone do endereço residencial",
  comprovante_endereco_comercial: "Conta de luz, água ou telefone do endereço comercial (validado com CNPJ)",
  foto_externa: "Foto externa do estabelecimento comercial",
  foto_interna: "Foto interna do estabelecimento",
  video_apresentacao: "Vídeo curto apresentando o negócio",
  redes_sociais: "Screenshot de perfil de rede social (Instagram, Facebook, LinkedIn)",
  documentos_avalista: "Documentos de quem avaliza: RG ou CNH, comprovante de endereço, comprovante de renda",
  outros: "Outros documentos relevantes (contratos, certidões, declarações, etc.)",
}

// ── Avalista ─────────────────────────────────────────────────────────────
//
// Task wdwxndxp5u (fase 2 da wdwxndxp5n). O que foi LIDO no documento de quem
// avaliza o pedido — uma linha por avalista, em `credit_v2_lead_avalistas`.
//
// 🚨 Não existe aqui, e não pode passar a existir, nenhum campo de conferência
// (`nome_confere`, `cpf_confere` ou equivalente). O avalista não valida e não
// invalida o solicitante: nada dele é cruzado com nada dele. Um campo desses
// nesta interface seria o começo do defeito que a tarefa existe para matar.
//
// Campo `null` quer dizer "não veio no documento", e a tela precisa dizer isso
// com essas palavras — em branco se lê como conferido.
export interface LeadAvalista {
  id: string
  lead_id: string
  tenant_id: string
  nome: string | null
  cpf: string | null
  rg: string | null
  /** Como impresso no documento (DD/MM/AAAA). */
  data_nascimento: string | null
  nome_mae: string | null
  nome_pai: string | null
  tipo_documento: string | null
  lido_em: string
}

/**
 * O que a NOSSA base já sabe sobre esse CPF, dentro da MESMA unidade.
 *
 * Sem consulta paga a birô — nem automática, nem sob demanda. É só o que já
 * está aqui dentro e que quem olha o Dossiê já poderia ver por outro caminho.
 */
export interface AvalistaNaNossaBase {
  /** É cliente SOMA desta unidade. */
  eCliente: boolean
  /** Já existe como lead desta unidade. */
  eLead: boolean
  /**
   * Situação de pagamento do cliente, no vocabulário que a tela de Cobranças
   * já usa. `null` quando não é cliente ou quando a situação não é conhecida.
   */
  situacao: "inadimplente" | "em_dia" | "concluido" | null
}

// ── Document Status ──────────────────────────────────────────────────────

export type DocumentStatus = "pendente" | "em_analise" | "aprovado" | "reprovado"

export const DOCUMENT_STATUS_LABELS: Record<DocumentStatus, string> = {
  pendente: "Pendente",
  em_analise: "Em Análise",
  aprovado: "Aprovado",
  reprovado: "Reprovado",
}

export const DOCUMENT_STATUS_COLORS: Record<DocumentStatus, string> = {
  pendente: "slate",
  em_analise: "blue",
  aprovado: "emerald",
  reprovado: "red",
}

export interface LeadDocument {
  id: string
  lead_id: string
  tenant_id: string
  doc_type: string
  storage_path: string
  original_filename: string | null
  status: DocumentStatus
  uploaded_at: string
  uploaded_by: string | null
  reviewed_at: string | null
  reviewed_by: string | null
  notas: string | null
  // #170: soft delete — documento apagado fica com deleted_at preenchido.
  // Arquivo no Storage permanece (restauração reversível).
  deleted_at: string | null
  deleted_by: string | null
}

// ── Platform Analysis (gig economy) ──────────────────────────────────────

export interface PlatformAnalysis {
  plataforma: string
  periodo: string
  resumo_financeiro: {
    receita_bruta: number
    taxas_plataforma: number
    receita_liquida: number
    renda_mensal_estimada: number
  }
  detalhes: {
    total_operacoes: number
    media_por_operacao: number
    dias_ativos: number
    frequencia_semanal: number
  }
  renda_comprovada: boolean
  regularidade: "alta" | "media" | "baixa" | "insuficiente"
  resumo_ia: string
  analisado_em: string
  confidence: number
}

// ── Social Media Analysis ────────────────────────────────────────────────

export type SocialVerificationStatus = "confirmado" | "nao_identificado" | "nao_corresponde"

export interface SocialVerification {
  campo: string
  status: SocialVerificationStatus
  detalhe: string
  fonte: string
}

export interface SocialMediaAnalysis {
  plataformas_analisadas: string[]
  verificacoes: SocialVerification[]
  score_social: number
  resumo_ia: string
  analisado_em: string
  confidence: number
  /** Resumo IA por plataforma (key = platform id: instagram, facebook, etc.).
   *  Opcional pra retrocompat com análises antigas que só tinham resumo_ia
   *  monolítico. Quando presente, o dossiê usa este por canal. (#156) */
  resumos_por_plataforma?: Record<string, string>
  /** Por plataforma: true se a análise teve enrichment factual (Apify/Places/
   *  HTML/screenshot), false se Gemini analisou só a URL nua. Quando false, a
   *  confidence é cortada pela metade no backend. (#156 A3) */
  enrichment_status?: Record<string, boolean>
}

// ── Extrato Bancário Analysis (Parecer Consultivo por Categorias) ─────────
//
// Schema v2 (tasks #159 + #151, 2026-05-20):
//   - Nova categoria `padroes_suspeitos` (agiotagem, stacking, atravessador/laranja)
//   - Nova seção `sinais_positivos` (bonifica leads saudáveis no IVE — BON-EXT-01..07)
//   - Triagem de documento (`tipo_documento`, `documentacao_insuficiente`)
//   - `risco_geral` ponderado pelo Gemini, com novo valor "INDETERMINADO"
//   - Apostas com `tier` (esporadico/moderado/critico)
//   - Pesos diferenciados no IVE calculator (apostas/agiotagem/ESCs > confusão patrimonial)
//
// Schema aditivo: campos antigos (`confusao_patrimonial`, `constancia_receita_diaria`,
// etc.) preservados pra retrocompat com análises pré-v2.

export type ExtratoRisco = "ALTO" | "MEDIO" | "BAIXO"
export type ExtratoRiscoGeral = ExtratoRisco | "INDETERMINADO"
export type ExtratoApostasTier = "ausente" | "esporadico" | "moderado" | "critico"

export type ExtratoTipoDocumento =
  | "extrato_bancario"
  | "extrato_parcial"
  | "screenshot_isolado"
  | "foto_kyc"
  | "cupom_nfe"
  | "boleto_utilidade"
  | "comprovante_isolado"
  | "parcial_ilegivel"
  | "extrato_de_terceiro"

export type ExtratoContatoTipo =
  | "possivel_agiotagem"
  | "atravessador_conduit"
  | "outra_esc"
  | "gambling_front"
  | "indefinido"

export type ExtratoAgiotagemPattern =
  | "A_bidirecional"
  | "B_saidas_sem_entrada"
  | "C_juros_diarios"
  | "conduit"
  | "stacking"

export interface ExtratoOperacaoEmprestimo {
  data: string
  descricao: string
  valor: number
  pessoa?: string
}

export interface ExtratoOperacaoAposta {
  data: string
  descricao: string
  valor: number
  casa_de_aposta: string
}

export interface ExtratoContatoSuspeito {
  nome: string
  tipo: ExtratoContatoTipo
  pattern: ExtratoAgiotagemPattern
  entradas: { data: string; valor: number }[]
  saidas: { data: string; valor: number }[]
  total_entradas: number
  total_saidas: number
  observacao: string
}

export interface ExtratoStackingEvento {
  data: string
  fonte: string
  valor: number
}

export interface ExtratoCategoriaPadroesSuspeitos {
  risco: ExtratoRisco
  justificativa: string
  contatos_suspeitos: ExtratoContatoSuspeito[]
  stacking_temporal_detectado: boolean
  stacking_eventos: ExtratoStackingEvento[]
  atravessador_conduit_detectado: boolean
}

/**
 * ESC concorrente detectada no extrato. A análise por IA devolve duas formas
 * para o mesmo campo: só o nome, ou nome + quanto sai por mês. As duas existem
 * em produção — ler sempre pelos dois caminhos.
 */
export type ExtratoConcorrente = string | { nome?: string; valor_saida_mensal?: number }

export interface ExtratoCategoriaEmprestimos {
  risco: ExtratoRisco
  justificativa: string
  operacoes: ExtratoOperacaoEmprestimo[]
  /** v2: ESCs concorrentes / financeiras detectadas como pagas pelo lead. */
  escs_concorrentes_detectadas?: ExtratoConcorrente[]
}

export interface ExtratoCategoriaApostas {
  risco: ExtratoRisco
  justificativa: string
  operacoes: ExtratoOperacaoAposta[]
  /** v2: tier de severidade (esporadico = recreativo, critico = vício). */
  tier?: ExtratoApostasTier
  /** v2: lista de plataformas + gateways de gambling-front detectados. */
  plataformas_detectadas?: string[]
  /** v2: estimativa de volume mensal em apostas. */
  volume_mensal_estimado?: number
  /** v2: padrão noturno 02-06h (forte indício de vício). */
  padrao_noturno?: boolean
}

export interface ExtratoCategoriaFluxoCaixa {
  risco: ExtratoRisco
  justificativa: string
  /** Legado v1 — mantido pra retrocompat. */
  constancia_receita_diaria: boolean
  /** Legado v1 — mantido pra retrocompat. */
  saldo_proximo_zero_frequente: boolean
  percentual_comprometimento: number
  /** v2: saldo apertado é NORMAL pra micro empresa (não red flag isolado). */
  saldo_apertado_normal?: boolean
  /** v2: hemorragia real = saldo zera sem entradas no mesmo período. */
  hemorragia_real?: boolean
  /** v2: % das vendas antecipadas via maquininha. */
  antecipacao_maquininha_pct?: number
}

export interface ExtratoCategoriaOutrosRiscos {
  risco: ExtratoRisco
  justificativa: string
  /** Legado v1 — mantido pra retrocompat. v2: descritivo, NÃO é red flag. */
  confusao_patrimonial: boolean
  dependencia_antecipacao: boolean
  /** v2: alias descritivo (mistura PF/PJ é normal pra micro empresa). */
  confusao_patrimonial_observada?: boolean
}

export interface ExtratoCategorias {
  /** v2: nova categoria principal (agiotagem, stacking, atravessador). */
  padroes_suspeitos?: ExtratoCategoriaPadroesSuspeitos
  emprestimos: ExtratoCategoriaEmprestimos
  apostas: ExtratoCategoriaApostas
  fluxo_caixa: ExtratoCategoriaFluxoCaixa
  outros_riscos: ExtratoCategoriaOutrosRiscos
}

export interface ExtratoSinaisPositivos {
  /** BON-EXT-01: extrato real anexado (tipo_documento extrato_bancario/extrato_parcial). +5 IVE. */
  extrato_anexado_valido: boolean
  /** BON-EXT-02: zero red flags (sem agiotagem/stacking/apostas crítico/laranja/hemorragia). +10 IVE. */
  ausencia_total_red_flags: boolean
  /** BON-EXT-03: entradas em ≥60% dos dias úteis. +5 IVE. */
  constancia_receita: boolean
  /** BON-EXT-04: ≥20 CPFs distintos como pagadores PIX. +3 IVE. */
  diversidade_clientes_pf: boolean
  /** BON-EXT-05: saldo_final ≥ saldo_inicial × 1,05. +3 IVE. */
  saldo_crescente: boolean
  /** BON-EXT-06: saldo médio diário > R$1k OU > 30% da receita mensal. +3 IVE. */
  capital_giro_saudavel: boolean
  /** BON-EXT-07: período ≥ 90 dias. +2 IVE. */
  historico_bancario_longo: boolean
  /** Descritivo: usa ≥3 canais (PIX + maquininha + boleto). Não bonifica pontos. */
  uso_multicanal: boolean
  justificativa: string
}

export interface ExtratoAnalysis {
  /** v2: classificação do tipo de documento (extrato, kyc, cupom...). */
  tipo_documento?: ExtratoTipoDocumento
  /** v2: true quando pacote não tem extrato real (foto KYC, cupom, etc.). */
  documentacao_insuficiente?: boolean
  /** v2: sempre true — flag explícita de que análise é micro empresa-aware. */
  contexto_micro_empresa?: boolean
  /** v2: descrição curta da natureza do negócio observada. */
  operacao_descricao?: string
  arquivos: {
    banco: string
    titular: string
    periodo: string
    total_entradas: number
    total_saidas: number
    saldo_final: number | null
    /** wdwxndzh00: caminho no Storage do arquivo desta linha (leitura em partes). */
    path?: string
    /** wdwxndzh00: nome do arquivo como foi enviado. */
    nome_original?: string | null
    paginas_total?: number
    paginas_lidas?: number
    movimentos?: number
    /** wdwxndzh01: CPF ou CNPJ do titular como aparece no extrato. */
    documento_titular?: string
    /** wdwxndzh01: o titular é o cliente ou a empresa dele? */
    titular_confere?: "confere" | "nao_confere" | "indefinido"
    titular_motivo?: string
  }[]
  categorias: ExtratoCategorias
  /** v2: novo valor "INDETERMINADO" quando documentacao_insuficiente=true. */
  risco_geral: ExtratoRiscoGeral
  /** v2: motivos citáveis do risco_geral (1 frase cada). */
  razoes_risco_geral?: string[]
  /** v2: sinais positivos pra bonificar lead saudável no IVE. */
  sinais_positivos?: ExtratoSinaisPositivos
  recomendacao: string
  resumo_ia: string
  analisado_em: string
  confidence: number
  /** #203: arquivos travados por senha que ficaram de fora da leitura. */
  arquivos_protegidos?: ArquivoExtratoProtegido[]
  /** Leitura única: paths que de fato geraram esta análise (exclui travados). */
  source_paths?: string[]
  /** #203: extrato destravado tinha mais páginas que o limite — a IA leu só as primeiras. */
  extrato_truncado?: boolean
  /** #203: total de páginas do extrato destravado quando truncado. */
  extrato_paginas_total?: number
  /** Causa da falha de leitura: "arquivo_grande" (pesado demais para abrir),
   *  "timeout" (a leitura não terminou no prazo), "leitura_falhou". */
  motivo_falha?: string
  /** wdwxndzh00: leitura rodando em segundo plano. O parecer anterior continua
   *  valendo até o novo chegar; com mais de 10 minutos, conta como falha. */
  leitura_em_andamento?: ExtratoMarcadorDeLeitura | null
  /** wdwxndzh00: como foi a última leitura (em partes). */
  leitura?: ExtratoLeitura | null
  /** wdwxndzh00: arquivos que ficaram de fora sem ser por senha. */
  arquivos_nao_lidos?: { path: string; original_filename: string | null; motivo: "arquivo_grande" | "nao_baixou" | "fora_do_limite" }[]
  /** wdwxndzh01: conferência do titular contra o cadastro do lead (a tela só lê). */
  conferencia_titular?: {
    resultado: "confere" | "nao_confere" | "indefinido"
    arquivos_de_outro_titular: { path: string; nome_original: string | null; titular: string }[]
  } | null
  /**
   * wdwxndzraf: entradas e saídas por semana, mês e pessoa, somadas em código
   * sobre a lista transcrita. Ausente = parecer lido antes desta conta existir
   * (a tela convida a reler); nulo = leitura sem movimentação (nada a mostrar).
   */
  fluxo?: ExtratoFluxo | null
}

/** wdwxndzraf: uma semana (segunda a domingo) ou um mês, cortado pelo período. Datas AAAA-MM-DD. */
export interface ExtratoFluxoLinha {
  inicio: string
  fim: string
  parcial: boolean
  entrou: number
  saiu: number
  resultado: number
  movimentos: number
}

/** wdwxndzraf: uma pessoa ou empresa, somando todas as vezes que aparece. */
export interface ExtratoFluxoPessoa {
  nome: string
  entrou: number
  saiu: number
  diferenca: number
  entradas: number
  saidas: number
  /** Conta do próprio cliente: titular do extrato, cliente, empresa ou sócio. */
  conta_propria: boolean
}

export interface ExtratoFluxoSoma {
  entrou: number
  saiu: number
  entradas: number
  saidas: number
}

export interface ExtratoFluxoDestaque {
  data: string | null
  valor: number
  nome: string
}

/** wdwxndzraf: saldo inicial + entradas − saídas comparado com o saldo final, por arquivo. */
export interface ExtratoFluxoConferencia {
  nome_original: string | null
  banco: string
  saldo_inicial: number | null
  saldo_final: number | null
  entrou: number
  saiu: number
  saldo_calculado: number | null
  diferenca: number | null
  situacao: "fecha" | "nao_fecha" | "indisponivel"
  motivo?: "sem_saldos" | "leitura_parcial"
  /** wdwxndzve9: o sistema já releu páginas deste arquivo. Só aparece quando é verdade. */
  relida?: boolean
}

/** wdwxndzraf: o resumo de entradas e saídas do extrato (`analise_extrato.fluxo`). */
export interface ExtratoFluxo {
  versao: number
  periodo: { inicio: string; fim: string; dias: number } | null
  movimentos: number
  sem_data: { movimentos: number; entrou: number; saiu: number }
  totais: { entrou: number; saiu: number; resultado: number }
  media_semanal: { entrou: number; saiu: number; resultado: number } | null
  nao_venda: {
    conta_propria: number
    emprestimo: number
    resgate: number
    total: number
    saiu_conta_propria: number
  }
  semanas: ExtratoFluxoLinha[]
  meses: ExtratoFluxoLinha[]
  pessoas: ExtratoFluxoPessoa[]
  outras: (ExtratoFluxoSoma & { quantidade: number }) | null
  sem_pessoa: ExtratoFluxoSoma | null
  maiores_entradas: ExtratoFluxoDestaque[]
  maiores_saidas: ExtratoFluxoDestaque[]
  conferencia: ExtratoFluxoConferencia[]
}

/** wdwxndzh00: marcador gravado enquanto o extrato é lido em segundo plano. */
export interface ExtratoMarcadorDeLeitura {
  id: string
  desde: string
  paginas: number
  arquivos: number
  caminhos: string[]
}

/** wdwxndzh00: resumo da última leitura do extrato. */
export interface ExtratoLeitura {
  modo?: "partes"
  caminhos?: string[]
  paginas_total?: number
  paginas_lidas?: number
  partes?: number
  partes_com_falha?: number
  movimentos?: number
  chamadas?: number
  /** Alguma parte não foi lida: o Recalcular IVE relê. */
  parcial?: boolean
  /** O teto de páginas cortou o extrato (lidas as mais recentes). */
  limite_aplicado?: boolean
  limite_paginas?: number
  duracao_ms?: number
  custo_estimado_usd?: number
  /** wdwxndzve9: a conferência de completude e a releitura com o outro modelo (a tela não lê). */
  releitura?: ExtratoReleitura
}

/** wdwxndzve9: partes que pareciam ter perdido linhas e foram relidas. Só números e códigos. */
export interface ExtratoReleitura {
  versao: number
  suspeitas: number
  relidas: number
  trocadas: number
  sem_tempo: boolean
  movimentos_antes: number
  movimentos_depois: number
  motivos: { poucas_linhas: number; muitas_linhas: number; salto_de_datas: number; saldo_nao_fecha: number }
  partes: {
    arquivo: number
    /** Páginas da parte, contando de 1: [primeira, última]. */
    paginas: [number, number]
    motivos: ("poucas_linhas" | "muitas_linhas" | "salto_de_datas" | "saldo_nao_fecha")[]
    modelo_original: string | null
    modelo_releitura: string | null
    movimentos_original: number
    movimentos_releitura: number | null
    ficou: "original" | "releitura"
    falhou?: true
    sem_tempo?: true
  }[]
}

/** #203: extrato protegido por senha — alimenta o campo de desbloqueio no Dossiê. */
export interface ArquivoExtratoProtegido {
  path: string
  original_filename: string | null
  status: "needs_password" | "wrong_password" | "error"
}

// ── Video Analysis (Fase 2C — IVE 3.0 + V3 Microcrédito #22) ──────────────

export interface AnaliseVideo {
  // ── V1 — backward compat (IVE calculator) ─────────────────────────────
  /** Fachada visível no vídeo bate com a imagem Street View do endereço? null = indeterminado/sem Street View */
  fachada_confere_street_view: boolean | null
  /** O que aparece no vídeo é compatível com o CNAE declarado? null = não conseguiu avaliar */
  natureza_compativel_cnae: boolean | null
  /** Performance natural do lead (sem ensaio/leitura/terceiros). true = natural, false = suspeita */
  performance_natural: boolean | null
  /** Quando performance_natural=false, explica o que foi detectado */
  performance_suspeita_motivo: string | null
  /** Confiança V1 (0-1). Em V3 usa "confidence" (0-100). */
  confianca: number
  /** Resumo curto do que Gemini viu no vídeo (até 400 chars). */
  analise_descritiva: string
  /** ISO 8601 — quando a análise rodou */
  analisado_em: string

  // ── V3 — Microcrédito/Negativados (#22) ───────────────────────────────
  /** Score de risco 0-100 (100 = ALTO risco). Null em legados pré-V3. */
  risk_score?: number | null
  /** Score de consistência operacional 0-100 (100 = EXCELENTE). */
  operational_consistency_score?: number | null
  /** Atividade comercial vista no vídeo. */
  atividade_observada_video?: string
  /** Segmento declarado no dossiê — pro cruzamento. */
  atividade_declarada_dossie?: string
  /** Atividade vídeo bate com declarada? FLAG CRÍTICO se false. */
  atividade_match?: boolean | null
  /** Sinais comportamentais detectados (hesitação, leitura, etc.). */
  psychological_flags?: string[]
  /** Contradições entre fala e dados do dossiê. */
  data_contradictions?: string[]
  /** Observações gerais do Gemini. */
  observations?: string[]
  /** Fatores de risco identificados. */
  risk_factors?: string[]
  /** Indicadores positivos (microcrédito). */
  positive_indicators?: string[]
  /** Confidence V3 (0-100). V1 usa "confianca" (0-1) — legado. */
  confidence?: number | null
  /** Explicação em 3 linhas para o comitê de crédito. */
  verdict_reasoning?: string
  /** RAG global_risk_patterns aplicado? V1 sempre false. */
  networkContextApplied?: boolean
  /** "v3" pra schemas atuais. Undefined em legados. */
  schema_version?: "v3"
}

/** Resposta do edge validate-video-kyc (Estágio 1 gating síncrono). */
export interface KycValidationResult {
  is_valid: boolean
  checks: {
    shows_exterior?: boolean
    shows_interior?: boolean
    shows_inventory?: boolean
    is_continuous_shot?: boolean
    video_duration?: number
    face_detected?: boolean
    document_detected?: boolean
    transcription?: string
    phrase_match?: boolean
    liveness_score?: number
    audio_quality?: "good" | "fair" | "poor" | "inaudible"
  }
  failure_reasons: string[]
  feedback_for_user: string
  video_type: "selfie" | "local"
  model_used: string
  validated_at: string
  video_size_bytes: number
}

// ── Places Analysis (Fase 2B — IVE 3.0) ─────────────────────────────────

export interface AnalisePlaces {
  /** true se algum resultado bateu com nome+cidade */
  found: boolean
  /** place_id Google (opaco) */
  place_id: string | null
  /** Nome retornado pelo Google */
  nome_encontrado: string | null
  /** Endereço formatado retornado */
  endereco_encontrado: string | null
  /** Rating agregado (0-5) */
  rating: number | null
  /** Nº de reviews */
  total_reviews: number | null
  /** Categorias Google (ex: ["restaurant","food"]) */
  tipo_estabelecimento: string[] | null
  /** Website oficial, se registrado no Places */
  site_oficial: string | null
  /** Telefone formatado */
  telefone: string | null
  /** Comparação fuzzy do endereço do Places com o declarado do CNPJ */
  endereco_confere_cnpj: boolean | null
  /** ISO 8601 — quando a análise rodou */
  analisado_em: string
}

// ── Street View Analysis (Fase 2A — IVE 3.0) ─────────────────────────────

export interface AnaliseStreetView {
  /** URLs das imagens Street View capturadas em diferentes headings */
  imagens: Array<{
    url: string
    heading: number
    capturado_em: string
  }>
  /** true = fachada comercial detectada; false = residencial; null = indeterminado/sem cobertura */
  fachada_comercial: boolean | null
  /** Categoria detectada pelo Gemini Vision */
  tipo_detectado: "comercial" | "residencial" | "misto" | "indeterminado"
  /** Número da fachada lido na imagem (se visível) */
  numero_detectado: string | null
  /** Se numero_detectado bate com o número do endereço declarado */
  numero_bate: boolean | null
  /** Confiança da análise IA (0 a 1) */
  confianca: number
  /** Texto livre da análise Gemini */
  analise_descritiva: string
  /** ISO 8601 — quando a análise rodou */
  analisado_em: string
}

/** Nível de risco da rede 4ESC (API partner-score-lookup).
 *  "X" = histórico incompleto/congelado → neutro (não influencia a nota IVE). */
export type FourEscNivel = "A" | "B" | "C" | "D" | "X"

/**
 * Cache da consulta de score na rede 4ESC (credit_v2_leads.fouresc_score).
 * Cópia do shape canonical em supabase/functions/_shared/fouresc-score.ts.
 */
export interface FourEscScoreCache {
  found: boolean
  nivel: FourEscNivel | null
  nivel_label: string | null
  razoes: Array<{ label: string }>
  source: "4esc" | "fallback_local"
  consultado_em: string
  tax_id_consultado: string
  tax_id_tipo: "cnpj" | "cpf"
  fallback_motivo?: string
}

/** #3.2 — UTM / campaign data captured at LP entry. */
export interface UtmData {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  gclid?: string   // Google Ads click id
  fbclid?: string  // Meta/Facebook click id
  referrer?: string
  landing_url?: string
  captured_at?: string
}

export interface Lead {
  id: string
  tenant_id: string
  column_id: string
  position: number
  assigned_to: string | null
  visitor_id: string | null
  session_id: string | null
  nome_completo: string
  cidade: string | null
  estado: string | null
  celular: string | null
  possui_cnpj: boolean | null
  possui_fachada: boolean | null
  valor_desejado: number | null
  parcela: string | null
  valor_parcela: number | null
  origem: LeadOrigem | null
  origens?: LeadOrigem[] | null
  latitude: number | null
  longitude: number | null
  cpf: string | null
  cnpj: string | null
  email: string | null
  nome_familiar: string | null
  telefone_familiar: string | null
  vendedor: string | null
  link_rg_cnh: string | null
  link_selfie_documento: string | null
  link_comprovante_endereco: string | null
  link_comprovante_endereco_residencial?: string | null
  link_comprovante_endereco_comercial?: string | null
  link_foto_externa: string | null
  link_foto_interna: string | null
  razao_social: string | null
  cnae: string | null
  situacao_cnpj: SituacaoCNPJ | null
  capital_social: number | null
  socios: Socio[] | null
  endereco_validado: EnderecoValidado | null
  processos: ProcessoJudicial[] | null
  protestos: unknown[] | null
  dividas: unknown[] | null
  score_pj: number | null
  score_pf: number | null
  ive_score: number | null
  ive_classificacao: IVEClassification | null
  ive_impedimentos: IVEImpedimento[] | null
  ive_bonificacoes: IVEFator[] | null
  ive_penalizacoes: IVEFator[] | null
  ive_nao_pontuados: IVEFator[] | null
  ive_teto_efetivo: number
  ive_analisado_em: string | null
  // IVE 3.0 — Versionamento e shadow mode (Fase 0.2)
  // Nota: ive_version tem DEFAULT '2.0' na DB, mas pode ser undefined antes do deploy da migration
  ive_version?: string
  ive_inputs_snapshot?: IVEInputsSnapshot | null
  ive_score_shadow?: number | null
  ive_classificacao_shadow?: string | null
  ive_shadow_version?: string | null
  ive_recalculado_em?: string | null
  last_ive_inputs_snapshot?: Record<string, unknown> | null
  last_dossier_regen_meta?: {
    decided_at: string
    blocks_regenerated: string[]
    blocks_reused: string[]
  } | null
  ive_outcome?: "aprovado_bom" | "aprovado_ruim" | "reprovado" | "pendente" | null
  ive_outcome_registrado_em?: string | null
  ive_outcome_registrado_por?: string | null
  // Enrichment fields (populated by BigDataCorp via webhook)
  data_abertura_empresa: string | null
  /**
   * Quando o BIRÔ conferiu essa situação pela última vez (`TaxIdStatusDate`)
   * — não quando a Receita mudou o status (esse é `TaxIdStatusRegistrationDate`,
   * que não capturamos). NÃO confundir com `data_abertura_empresa` — o Dossiê
   * exibia essa outra por engano até 21/08/2026.
   *
   * 🚨 CORREÇÃO (tarefa wdwxndxp5d, 06/10/2026): este comentário descrevia
   * `TaxIdStatusDate` como "quando a Receita fixou" — são campos diferentes.
   * Consulta ao vivo no birô (12/08/2026, achado de Bruno Zappa) confirmou os
   * dois valores no MESMO CNPJ: `TaxIdStatusRegistrationDate` = 06/07 (Receita
   * mudou), `TaxIdStatusDate` = 11/07 (birô capturou). O que esta coluna
   * guarda é o segundo: idade do RETRATO do birô, não a data do fato na
   * Receita — é o que deixa o analista saber que o dado pode estar velho.
   *
   * Coluna criada pela migration manual 255. `enrich-lead` grava junto com
   * `situacao_cnpj`, a cada consulta nova ao birô
   * (`supabase/functions/enrich-lead/situacao-do-cnpj.ts`). Lead consultado
   * antes disso fica `null` até a próxima consulta, e a tela mostra o traço.
   * Opcional no tipo porque o banco que ainda não tem a coluna devolve a
   * linha sem ela.
   */
  data_situacao_cnpj?: string | null
  nome_fantasia: string | null
  cnae_descricao: string | null
  email_contato_empresa: string | null
  cpf_status: string | null
  data_nascimento: string | null
  link_video_apresentacao: string | null
  // Person (CPF) enrichment — BigDataCorp basic_data + phones + emails + addresses
  nome_mae?: string | null
  nome_pai?: string | null
  genero?: string | null
  apelido?: string | null
  cpf_uf?: string | null
  rg?: string | null
  rg_orgao_emissor?: string | null
  rg_estado_emissor?: string | null
  indicacao_obito?: boolean | null
  data_status_cpf?: string | null
  data_cadastro_cpf?: string | null
  /**
   * Estado civil, como o birô escreve (`MaritalStatusData.MaritalStatus`).
   * Migration manual 305, tarefa wdwxndzqnw. Gravado só pela consulta completa
   * feita depois da mudança; a tela lê por `src/lib/estadoCivil.ts`.
   */
  estado_civil?: string | null
  /**
   * Estado da consulta aos birôs. `status: "completed"` é o ÚNICO sinal
   * confiável de que a consulta aconteceu.
   *
   * 🚨 Não dá para inferir isso das listas: medido em 03/09/2026, 330 leads têm
   * `processos: []` sem que a consulta tenha rodado. Lista vazia não prova que
   * o birô respondeu vazio — e a diferença entre "não há processo" e "ninguém
   * perguntou" é a diferença entre um parecer e um chute.
   *
   * `leitura_pessoa` diz qual leitura da pessoa a consulta fez, e
   * `releitura_pessoa_em` quando o Recalcular IVE releu só a pessoa (tarefa
   * wdwxndzffr). Ver `src/lib/leituraDaPessoa.ts`.
   */
  bigdatacorp_cache_meta?: {
    status?: string
    fetched_at?: string
    doc_key?: string
    leitura_pessoa?: number
    releitura_pessoa_em?: string
    /**
     * A consulta por parte (tarefa wdwxndzvyn): a chave, a data e o desfecho
     * da empresa e da pessoa, separados. Registro antigo não tem; o servidor
     * lê os dois formatos (`supabase/functions/enrich-lead/partes-da-consulta.ts`).
     */
    partes?: {
      empresa?: { chave: string; em: string; desfecho: string } | null
      pessoa?: { chave: string; em: string; desfecho: string } | null
    }
    /**
     * O que a Receita disse do CNPJ antes da consulta paga (tarefa wdwxndzvyp).
     * Com `status: "aguardando_decisao"`, a consulta completa ficou para a
     * decisão da pessoa. Ver `src/lib/consultaDosBiros.ts`.
     */
    porteiro?: {
      cnpj?: string
      consultado_em?: string
      decisao?: "segurar" | "seguir"
      motivos?: Array<"cnpj_baixado" | "fora_da_area">
      situacao?: string | null
      data_situacao?: string | null
      cidade?: string | null
      uf?: string | null
    }
  } | null
  telefones_bigdata?: string[] | null
  telefones_bigdata_qualified?: QualifiedContact[] | null
  emails_bigdata?: string[] | null
  emails_bigdata_qualified?: QualifiedContact[] | null
  // Company (CNPJ) enrichment — additional fields
  natureza_juridica?: string | null
  cnaes_secundarios?: { code: string; descricao: string }[] | null
  telefones_empresa?: string[] | null
  telefones_empresa_qualified?: QualifiedContact[] | null
  emails_empresa?: string[] | null
  emails_empresa_qualified?: QualifiedContact[] | null
  regime_tributario?: string | null
  porte_empresa?: string | null
  optante_simples?: boolean | null
  is_matriz?: boolean | null
  idade_empresa?: number | null
  // Person (CPF) enrichment — extra fields
  pis_nis?: string | null
  idade?: number | null
  nacionalidade?: string | null
  regiao_fiscal?: string | null
  enrichment_status: string | null
  enrichment_error: string | null
  foto_lead: string | null
  analise_documentos: Record<string, unknown> | null
  /** Respostas a campos personalizados definidos em sdr_config.perguntas_qualificacao.
   *  Estrutura: { "custom_<slug>": valor } onde valor pode ser string, number ou boolean.
   *  Task #139. */
  dados_personalizados?: Record<string, unknown> | null
  /** Tag livre copiada do webhook_tokens.lead_tag no momento da criação.
   *  Identifica a fonte do lead pra rastreabilidade. Task #138. */
  webhook_tag?: string | null
  /** #3.2 — UTM / campaign data captured at LP entry. */
  utm_data?: UtmData | null
  analise_ia?: Record<string, string | { texto: string; sentimento: "positivo" | "neutro" | "atencao" | "risco" }> | null
  // Bank statement analysis
  extrato_paths?: string[] | null
  analise_extrato?: ExtratoAnalysis | null
  // Platform extracts (gig economy)
  platform_doc_paths?: Record<string, string[]> | null
  analise_plataformas?: Record<string, PlatformAnalysis> | null
  // Social media analysis
  social_instagram?: string | null
  social_facebook?: string | null
  social_linkedin?: string | null
  social_google?: string | null
  social_site?: string | null
  social_media_paths?: string[] | null
  analise_redes_sociais?: SocialMediaAnalysis | null
  // Street View analysis (Fase 2A — IVE 3.0)
  analise_street_view?: AnaliseStreetView | null
  // Places analysis (Fase 2B — IVE 3.0)
  analise_places?: AnalisePlaces | null
  // Video analysis (Fase 2C — IVE 3.0)
  analise_video?: AnaliseVideo | null
  // Score na rede 4ESC (cache populado pelo run-ive-analysis)
  fouresc_score?: FourEscScoreCache | null
  // Renewal fields
  lead_type?: "new" | "renewal"
  parent_lead_id?: string | null
  renewal_valid_until?: string | null
  renewal_status?: "active" | "expired" | "pending_review" | null
  analise_renovacao?: RenewalValidation | null
  etapa_webhook: 0 | 1 | 2 | 3
  webhook_etapa1: WebhookEtapa1 | null
  webhook_etapa2: WebhookEtapa2 | null
  webhook_etapa3: WebhookEtapa3 | null
  is_existing_soma_client?: boolean
  created_at: string
  updated_at: string
  column?: KanbanColumn
  /** Summary do Vendedor (Profile básico — só os campos usados em UI).
   *  Populado pelo useLeads via RPC get_user_basic_summaries (migration
   *  089). Usa RPC SECURITY DEFINER em vez de JOIN sujeito a RLS pra
   *  funcionar em casos cross-tenant (Responsável de outra unidade,
   *  super_admin em viewAll). */
  assigned_user?: {
    id: string
    full_name: string | null
    avatar_url: string | null
    /** true quando assigned_to aponta pra um perfil que não existe mais
     *  (usuário excluído do sistema). A UI mostra "Ex-usuário" em vez de
     *  fingir que o lead está sem vendedor. Bug #217. */
    is_deleted?: boolean
  }
  /** IDs dos users na lista de Responsáveis (lead_responsibles). Não inclui
   *  o Vendedor (assigned_to). Populado pelo useLeads via 2-query merge.
   *  Ver task #112. */
  responsible_user_ids?: string[]
  /** Profile data dos Responsáveis (mesmo userId que responsible_user_ids).
   *  Necessário pra renderizar avatares em viewAll ("Todas as unidades"),
   *  onde members só contém o tenant atual e os Responsáveis de outras
   *  unidades não seriam resolvidos. Mesma motivação do assigned_user
   *  (Vendedor). Ver FB-2026-0507 / task #112 follow-up. */
  responsibles_summary?: Array<{
    userId: string
    fullName: string
    avatarUrl: string | null
  }>
}

/**
 * LeadFull extends Lead with additional fields used by the detail drawer/tabs.
 * These are extra UI/legacy fields not yet in the main Lead type.
 */
export interface LeadFull extends Lead {
  // UI aliases (derived from Lead fields in fetchLeadById)
  kanban_column_id?: string
  uf?: string | null
  bairro?: string | null
  cep?: string | null
  logradouro?: string | null
  numero?: string | null
  complemento?: string | null
  // Computed from arrays
  tem_processos_reu?: boolean
  tem_processos_requerido?: boolean
  tem_protestos?: boolean
  tem_dividas?: boolean
  // Aliases
  ive_calculado_em?: string | null
  seller_id?: string | null
  endereco_validado_bool?: boolean
  // From credit_v2_lead_enderecos table
  enderecos?: LeadEndereco[]
  // From credit_v2_lead_documents table
  documents?: LeadDocument[]
}

export interface LeadEndereco {
  id: string
  tipo: "comercial" | "residencial" | "relacionado"
  titulo: string
  /** "receita": o endereço de registro que a Receita deu antes da consulta completa (tarefa wdwxndzvyp). */
  fonte?: "bigdatacorp" | "ocr" | "webhook" | "manual" | "soma" | "receita"
  logradouro: string
  numero: string | null
  complemento: string | null
  bairro: string
  cidade: string
  estado: string
  cep: string
  latitude: number | null
  longitude: number | null
  is_principal?: boolean
}

// ========================================================
// IVE — MOTOR DE SCORE
// ========================================================

export interface IVEConfig {
  coletarVideos: boolean
  coletarProcessos: boolean
  coletarBureau: boolean
  coletarStreetView: boolean
  coletarRedesSociais: boolean
  cidadesAtendidas: string[]
  cnaeGenericos: string[]
  pesoBase: number
  /** Versão do motor IVE. Opcional; default '2.0'. Permite roteamento futuro interno. */
  version?: "2.0" | "3.0"
}

/**
 * Snapshot imutável dos inputs no momento do cálculo oficial.
 * Gravado em credit_v2_leads.ive_inputs_snapshot para auditoria e recálculo comparativo.
 */
export interface IVEInputsSnapshot {
  /** Versão do motor IVE que produziu o snapshot: '2.0' ou '3.0' */
  version: string
  /** ISO 8601 — quando o cálculo foi executado */
  calculated_at: string
  /** Cópia frozen da IVEConfig usada (inclui pesos, thresholds, flags) */
  config: IVEConfig
  /** Todos os campos relevantes do lead passados ao calculador */
  lead_inputs: Record<string, unknown>
  /**
   * Hash dos payloads BigDataCorp usados.
   * Permite detectar se dados de enriquecimento mudaram desde o cálculo.
   */
  bigdatacorp_digest?: {
    empresas?: string
    pessoas?: string
    bureau?: string
  }
}

export interface IVEResult {
  scoreFinal: number
  classificacao: IVEClassification
  impedimentos: IVEImpedimento[]
  bonificacoes: IVEFator[]
  penalizacoes: IVEFator[]
  nao_pontuados: IVEFator[]
  scoreBruto: number
  tetoEfetivo: number
}

// ========================================================
// ACTIVITY LOG E COMENTARIOS
// ========================================================

export type ActivityLogTipo =
  | "criado"
  | "movido"
  | "comentario"
  | "ive_atualizado"
  | "webhook_etapa"
  | "atribuido"
  | "documento_adicionado"
  | "editado"

export interface LeadActivityLog {
  id: string
  lead_id: string
  tenant_id: string
  user_id: string | null
  tipo: ActivityLogTipo
  descricao: string
  metadata: Record<string, unknown> | null
  created_at: string
  user?: Profile
}

export interface LeadComment {
  id: string
  lead_id: string
  tenant_id: string
  user_id: string
  conteudo: string
  mentions?: string[]
  created_at: string
  updated_at: string
  user?: Profile
}

// ========================================================
// USAGE LOGS
// ========================================================

export type UsageLogTipo =
  | "bigdatacorp"
  | "gemini"
  | "google_maps"
  | "god_mode"
  | "receita_federal"
  | "ibge"

export interface UsageLog {
  id: string
  tenant_id: string
  user_id: string
  tipo: UsageLogTipo
  metadata: Record<string, unknown> | null
  custo_usd: number | null
  created_at: string
}

// ========================================================
// CONTEXTO DO TENANT (Frontend)
// ========================================================

export type PermissionAction =
  | "view_funil"
  | "manage_esc_settings"
  | "create_esc"
  | "invite_users"
  | "view_super_admin"
  | "access_ai_config"
  | "manage_leads"
  | "view_all_leads"
  | "god_mode"

export interface TenantContextValue {
  user: import("@supabase/supabase-js").User | null
  profile: Profile | null
  role: AppRole
  isLoading: boolean
  isDataReady: boolean
  tenants: Tenant[]
  currentTenant: Tenant | null
  isInGodMode: boolean
  godModeTenant: Tenant | null
  setCurrentTenant: (tenant: Tenant) => void
  enterGodMode: (tenant: Tenant, redirectTo: string) => void
  exitGodMode: () => void
  /** Recarrega apenas o perfil do usuário atual (após avatar/nome/preferências). */
  refreshProfile: () => Promise<void>
  /**
   * Recarrega a lista de ESCs do usuário sem reload da página (após criar uma
   * ESC). Se `selectTenantId` for passado, foca essa ESC como escopo ativo —
   * usado pra cair direto na ESC recém-criada.
   */
  refreshTenants: (selectTenantId?: string) => Promise<void>
  signOut: () => Promise<void>
  can: (action: PermissionAction) => boolean
  hasRole: (role: AppRole) => boolean
  isAtLeast: (role: AppRole) => boolean
  hasLoadError: boolean
  retryLoad: () => void
  // Global scope (unit selector)
  selectedScope: string
  setSelectedScope: (scope: string) => void
  viewAll: boolean
  activeTenantId: string | undefined
  tenantMap: Record<string, string>
  /**
   * Mapa de cidades atendidas por tenant. Usado para mostrar alerta visual
   * em leads cuja `cidade` está fora da área do tenant — não bloqueia nada,
   * é só sinalização. Task #13.23 (2026-05-17): liberar travas de impedimento
   * geográfico. Ver `src/lib/leadCityArea.ts`.
   */
  tenantCitiesMap: Record<string, string[]>
  activeTenantColor: string | null
}

// ========================================================
// WEBHOOK TOKENS
// ========================================================

export interface WebhookToken {
  id: string
  tenant_id: string
  token: string
  label: string
  is_active: boolean
  /** Tag livre aplicada automaticamente em leads criados via esse webhook.
   *  Útil pra identificar a fonte (ex.: "Site da Braga"). Task #138. */
  lead_tag: string | null
  created_by: string | null
  created_at: string
  revoked_at: string | null
}

// ========================================================
// FORMS
// ========================================================

export interface ManualLeadFormData {
  cnpj: string
  cpf: string
  nome_completo: string
  celular: string
  email: string
  cidade: string
  estado: string
  valor_desejado: number
  parcela: string
  possui_fachada: boolean
  nome_familiar: string
  telefone_familiar: string
  vendedor: string
  origem: LeadOrigem
}

/** @deprecated Use CreateESCWizardData instead */
export interface NewESCFormData {
  cnpj: string
  name: string
  slug: string
  primary_color: string
  logo_url?: string
  allowed_cities: string[]
}

export interface InviteUserFormData {
  email: string
  role: AppRole
  tenant_id?: string
}

// ========================================================
// FILTROS E QUERIES
// ========================================================

export interface KanbanFilters {
  search: string
  ive_min?: number
  ive_max?: number
  assigned_to?: string
  origem?: LeadOrigem
  cidade?: string
  has_cnpj?: boolean
}

export interface SortParams {
  field: keyof Lead
  direction: "asc" | "desc"
}

// ========================================================
// RESPOSTAS DE API
// ========================================================

export interface ApiResponse<T> {
  data: T | null
  error: string | null
  success: boolean
}

export interface PaginatedResult<T> {
  data: T[]
  count: number
  page: number
  pageSize: number
  totalPages: number
}

// ========================================================
// CONFIGURACOES DO SISTEMA
// ========================================================

export const DEFAULT_KANBAN_COLUMNS: Omit<KanbanColumn, "id" | "tenant_id" | "created_at">[] = [
  // Obrigatórias (sistema)
  { slug: "novo_lead", name: "Novo Lead", color: "#8E8E93", status_order: 0, position: 0, is_default: true, is_terminal: false, is_client_stage: false },
  { slug: "cadastro_completo", name: "Cadastro Parcial", color: "#0A84FF", status_order: 1, position: 0, is_default: false, is_terminal: false, is_client_stage: false },
  { slug: "analisado_ive", name: "Analisado - IVE", color: "#BF5AF2", status_order: 2, position: 0, is_default: false, is_terminal: false, is_client_stage: false },
  // Sugeridas (editáveis)
  { slug: "necessita_visita", name: "Aguardando Validação", color: "#FF9F0A", status_order: 3, position: 0, is_default: false, is_terminal: false, is_client_stage: false },
  { slug: "emitir_contrato", name: "Emitir Contrato", color: "#5AC8FA", status_order: 3, position: 1, is_default: false, is_terminal: false, is_client_stage: false },
  { slug: "assinatura_contrato", name: "Assinatura de Contrato", color: "#30D158", status_order: 3, position: 2, is_default: false, is_terminal: false, is_client_stage: false },
  // Conclusões (terminais)
  { slug: "venda_realizada", name: "Venda Realizada", color: "#34C759", status_order: 4, position: 0, is_default: false, is_terminal: true, is_client_stage: false },
  { slug: "sem_interesse", name: "Sem Interesse", color: "#FF9F0A", status_order: -1, position: 0, is_default: false, is_terminal: true, is_client_stage: false },
  { slug: "reprovado", name: "Reprovado", color: "#FF453A", status_order: -1, position: 1, is_default: false, is_terminal: true, is_client_stage: false },
]

// ========================================================
// NOTIFICAÇÕES
// ========================================================

export type NotificationType =
  | "lead_assigned"
  | "lead_moved"
  | "lead_commented"
  | "lead_mention"
  | "ive_completed"
  | "renewal_expiring"
  | "renewal_approved"
  | "renewal_review_needed"
  | "documents_uploaded"
  | "soma_sync_failed"
  | "feedback_resolved"
  | "sdr_failed"
  // Grupo (lead duplicado entre unidades irmãs)
  | "lead_duplicado_grupo"
  // Quem já é cliente preencheu a landing page de novo (wdwxndzwur)
  | "cliente_voltou_pela_lp"
  // O lead já analisado voltou pela landing page e ficou na mesma coluna (wdwxndzwur)
  | "lead_voltou_pela_lp"
  // Faturamento
  | "credit_low"
  | "credits_purchased"
  | "credits_expiring"
  | "payment_confirmed"
  | "payment_overdue"
  | "credits_refunded"

// ========================================================
// FEEDBACK / SUPORTE
// ========================================================

export type FeedbackCategory = "bug" | "idea" | "feature" | "help"
export type FeedbackStatus = "open" | "in_progress" | "resolved" | "closed"
export type FeedbackPriority = "alta" | "normal" | "baixa"
export type FeedbackTranscriptionSource = "text" | "voice"

export interface FeedbackAiAnalysis {
  priority?: FeedbackPriority
  priority_rationale?: string
  /** Título técnico da task no ClickUp. Escrito para NÓS — não mostrar ao usuário. */
  summary?: string
  /**
   * 🚨 O que a pessoa que reportou lê quando o chamado fecha (18/08/2026).
   * Ausente nos chamados antigos, anteriores à separação — daí o fallback em
   * `tituloDoChamado`. Ver `supabase/functions/clickup-webhook/index.ts`.
   */
  summary_cliente?: string
  tags?: string[]
  probable_root_cause?: string | null
  suggested_implementation?: string | null
  affected_area?: "frontend" | "backend" | "database" | "integration" | "ux" | "unknown"
  user_intent_clarity?: "clear" | "ambiguous" | "unclear"
}

export interface FeedbackRequest {
  id: string
  user_id: string
  tenant_id: string
  category: FeedbackCategory
  message: string
  transcription_source: FeedbackTranscriptionSource
  screenshot_url: string | null
  screenshot_path: string | null
  page_url: string | null
  page_title: string | null
  user_agent: string | null
  viewport_w: number | null
  viewport_h: number | null
  ai_analysis: FeedbackAiAnalysis
  ai_priority: FeedbackPriority | null
  clickup_task_id: string | null
  clickup_task_url: string | null
  clickup_task_number: number | null
  clickup_sync_attempts: number
  clickup_sync_error: string | null
  status: FeedbackStatus
  reference_id: string
  created_at: string
  resolved_at: string | null
}

export interface AppNotification {
  id: string
  user_id: string
  tenant_id: string
  lead_id: string | null
  type: NotificationType
  payload: Record<string, unknown>
  read_at: string | null
  created_at: string
}

export interface RenewalValidation {
  video: {
    rosto_compativel: boolean
    negocio_ativo: boolean
    descricao: string
    confidence: number
  } | null
  comprovante: {
    endereco_corresponde: boolean
    data_emissao: string | null
    dentro_prazo: boolean
    detalhes: string
    confidence: number
  } | null
  aprovado: boolean
  motivo_rejeicao: string | null
}

// ========================================================
// MESSAGING & SDR
// ========================================================

export interface MessagingChannel {
  id: string
  tenant_id: string
  platform: "whatsapp" | "instagram" | "messenger"
  is_active: boolean
  meta_app_id: string | null
  meta_app_secret: string | null
  meta_access_token: string | null
  meta_phone_number_id: string | null
  meta_page_id: string | null
  meta_ig_account_id: string | null
  /** WhatsApp Business Account ID — necessário pra Graph API de templates.
   *  Adicionado pela migration 093 (task 10.8 fase 2). */
  meta_waba_id: string | null
  webhook_verify_token: string | null
  /** #10.4: distingue token colado manualmente (`manual`) vs Facebook Login
   *  for Business (`oauth`). Default `'manual'` para compatibilidade. */
  connection_method?: "manual" | "oauth"
  /** #10.4: long-lived user token (somente OAuth). Renovado automaticamente. */
  oauth_user_token?: string | null
  oauth_user_token_expires_at?: string | null
  oauth_scopes?: string[] | null
  oauth_meta_user_id?: string | null
  oauth_connected_by?: string | null
  oauth_connected_at?: string | null
  /** Estado do meta_access_token (migration 118).
   *  null = desconhecido / canal pré-feature.
   *  ok = última chamada bem-sucedida.
   *  expired = Meta retornou OAuthException 190 (admin precisa renovar).
   *  invalid = token rejeitado por outro motivo OAuth. */
  meta_token_status?: "ok" | "expired" | "invalid" | "unknown" | null
  meta_token_last_error_at?: string | null
  meta_token_last_error_message?: string | null
  /** Qualidade do número na Meta (webhook phone_number_quality_update):
   *  GREEN | YELLOW | RED. null = nunca recebido. */
  meta_phone_quality?: string | null
  meta_phone_quality_updated_at?: string | null
  /** Situação do número no WhatsApp, como o Meta devolve no campo `status`
   *  (CONNECTED, DISCONNECTED, BANNED...). Gravada só pela channel-health-check,
   *  e só com a credencial confirmada; null = ainda não conferida. Quem traduz
   *  para a tela é `@/lib/saudeDoCanal`. Migration 182, task wdwxndyy4w. */
  meta_phone_status?: string | null
  /** Última vez que a conferência leu a situação do número. */
  meta_phone_status_updated_at?: string | null
  /** Conferências seguidas em que o WhatsApp não informou a situação do número,
   *  com a credencial valendo. Zera quando a leitura volta; null = nenhuma
   *  tentativa registrada. Quem traduz para a tela é `@/lib/saudeDoCanal`.
   *  Migration 225, task wdwxndz0r0. */
  meta_phone_leituras_sem_resposta?: number | null
  /** Último alerta de conta enviado pela Meta (webhook account_alerts).
   *  Presente = a Meta sinalizou algo na conta (pausa, limite, bloqueio). */
  meta_account_alert?: Record<string, unknown> | null
  /** Fase 2c: nome amigável para o número (ex: "Atendimento Principal"). */
  display_name?: string | null
  /** Fase 2c: true = canal primário para a plataforma+tenant (índice parcial ≤1 por tenant+platform). */
  is_primary?: boolean
  created_at: string
  updated_at: string
}

/** #10.4: assets (Page, IG Business Account, WABA, Ad Account) que o user
 *  OAuth deu acesso ao app. Tenant_admin escolhe quais usar via AssetPickerModal. */
export interface MetaConnectedAsset {
  id: string
  tenant_id: string
  asset_type: "page" | "ig_account" | "whatsapp_waba" | "ad_account"
  meta_asset_id: string
  asset_name: string | null
  asset_metadata: Record<string, unknown>
  page_access_token: string | null
  selected: boolean
  connected_via_channel_id: string | null
  created_at: string
  updated_at: string
}

export interface Conversation {
  id: string
  tenant_id: string
  lead_id: string | null
  platform: "whatsapp" | "instagram" | "messenger"
  external_id: string
  phone_number: string | null
  initial_phone_number: string | null
  display_name: string | null
  status: "active" | "waiting_agent" | "closed" | "bot_paused"
  last_customer_message_at: string | null
  window_expires_at: string | null
  assigned_to: string | null
  sdr_context: Record<string, unknown>
  dados_coletados: Record<string, unknown>
  created_at: string
  updated_at: string
  // Joined fields
  lead?: LeadFull
  last_message?: Message
  unread_count?: number
}

export interface Message {
  id: string
  conversation_id: string
  tenant_id: string
  direction: "inbound" | "outbound"
  sender_type: "customer" | "bot" | "agent" | "system"
  sender_id: string | null
  content_type: "text" | "audio" | "image" | "document" | "video" | "template" | "interactive" | "location" | "sticker" | "reaction"
  content_text: string | null
  media_url: string | null
  media_mime: string | null
  audio_transcript: string | null
  external_message_id: string | null
  delivery_status: "pending" | "sent" | "delivered" | "read" | "failed"
  metadata: Record<string, unknown>
  created_at: string
}

export interface SDRConfig {
  id: string
  tenant_id: string
  is_active: boolean
  nome_agente: string
  /** @deprecated #183: campo removido da UI, mantido opcional pra retrocompat de payloads antigos. Backend ignora. */
  saudacao_inicial?: string | null
  tom_conversa: "formal" | "profissional_amigavel" | "casual"
  instrucoes_custom: string | null
  prompt_base: string | null
  dados_obrigatorios: string[]
  dados_opcionais: string[]
  /** Campos personalizados definidos pelo tenant admin — task #139.
   *  Cada item: { key, pergunta, tipo, opcoes? } onde:
   *   - key:      slug prefixado com 'custom_' (auto-gerado da pergunta)
   *   - pergunta: frase exata que o agente SDR vai fazer ao lead
   *   - tipo:     text | number | boolean | select
   *   - opcoes:   lista de valores válidos (apenas pra tipo=select) */
  perguntas_qualificacao: Array<{
    key?: string
    pergunta: string
    tipo?: "text" | "number" | "boolean" | "select" | string
    opcoes?: string[]
  }>
  criterios_desqualificacao: string[] | null
  max_mensagens_sem_resposta: number
  tempo_espera_minutos: number
  mensagem_encerramento: string | null
  auto_create_lead: boolean
  horario_inicio: string
  horario_fim: string
  dias_semana: number[]
  mensagem_fora_horario: string | null
  atendimento_24h: boolean
  /** Lembretes para quem parou de responder (wdwxne067k, migration 300). Nasce desligado. */
  lembretes_ligado?: boolean
  lembretes_quantidade?: number
  lembretes_primeiro_minutos?: number
  lembretes_segundo_minutos?: number
  /** Texto próprio da unidade por mensagem; chave ausente = texto sugerido. */
  lembretes_textos?: Record<string, string>
  created_at: string
  updated_at: string
}

export interface SDRInstruction {
  id: string
  tenant_id: string
  sdr_config_id: string
  nome: string
  is_active: boolean
  canais: Array<"whatsapp" | "instagram" | "messenger">
  tipo_gatilho: "palavras_chave" | "contexto"
  palavras_chave: string[] | null
  contexto_descritivo: string | null
  tipo_resposta: "mensagem_padrao" | "instrucao"
  mensagem_padrao: string | null
  instrucao_resposta: string | null
  prioridade: number
  created_at: string
  updated_at: string
}

// ========================================================
// SOMA — Importação & Mapa de Clientes
// ========================================================

export type SomaClientStatus = "em_dia" | "inadimplente" | "concluido"
// "outro_encerramento": etapa de encerramento que a unidade criou. Sem ela, o
// lead de "Desistiu" ou "Cidades nao Atendidas" caia no rotulo "Reprovado",
// afirmando na tela um desfecho que nao aconteceu. Ele nao foi ganho — mas
// dizer POR QUE nao foi ganho e outra coisa.
export type FunnelDisplayStatus =
  | "funil_vendas"
  | "venda_realizada"
  | "sem_interesse"
  | "reprovado"
  | "outro_encerramento"
export type MapDisplayStatus = SomaClientStatus | FunnelDisplayStatus
export type SomaGeoStatus = "pending" | "valid" | "invalid"
export type SomaAddressSource = "pending" | "csv" | "brasil_api" | "manual" | "bigdatacorp" | "soma"
export type SomaImportType = "base" | "risk"
export type SomaImportStatus = "processing" | "completed" | "failed" | "partial"
export type SomaSyncStatus = "never" | "ok" | "failed" | "running"
export type SomaSyncRunStatus = "running" | "ok" | "failed" | "partial"
export type SomaSyncTrigger = "manual" | "cron" | "wizard" | "retry"

export interface SomaConnection {
  tenant_id: string
  url: string
  username: string
  password_secret_id: string
  soma_company_name: string | null
  sync_enabled: boolean
  sync_status: SomaSyncStatus
  last_sync_at: string | null
  last_sync_counters: Record<string, unknown>
  last_error_message: string | null
  consecutive_failures: number
  created_at: string
  updated_at: string
}

export interface SomaSyncRun {
  id: string
  tenant_id: string
  trigger: SomaSyncTrigger
  status: SomaSyncRunStatus
  started_at: string
  completed_at: string | null
  duration_ms: number | null
  counters: Record<string, unknown>
  error_message: string | null
  triggered_by: string | null
}

export type SomaMatchConfidence = "high" | "medium" | "low" | "none"

export interface SomaCompanyInfo {
  name: string
  taxId: string | null
  match: { confidence: SomaMatchConfidence; reason: string }
}

export interface SomaTestResult {
  ok: boolean
  fullName?: string | null
  companies?: SomaCompanyInfo[]
  suggested?: { company: string; confidence: SomaMatchConfidence; reason: string } | null
  permissions?: { customer: boolean; operacao: boolean; recebivel: boolean }
  sampleCounts?: Record<string, number>
  code?: string
  message?: string
}

export interface SomaCrossTenantMatch {
  tenant_id: string
  tenant_name: string
  tenant_color: string | null
  status: SomaClientStatus
  overdue_days: number
  saldo_total: number | null
}

export interface SomaClient {
  id: string
  tenant_id: string
  lead_id: string | null
  cpf_cnpj: string
  name: string
  phone: string | null
  seller: string | null
  saldo_total: number
  saldo_principal: number
  saldo_juros: number
  status: SomaClientStatus
  overdue_amount: number
  overdue_total: number
  overdue_days: number
  overdue_installments: number
  last_due_date: string | null
  operation_codes: string[] | null
  full_address: string | null
  logradouro: string | null
  numero: string | null
  bairro: string | null
  cidade: string | null
  uf: string | null
  cep: string | null
  address_source: SomaAddressSource
  lat: number | null
  lng: number | null
  geo_status: SomaGeoStatus
  last_base_import: string | null
  last_risk_import: string | null
  last_risk_seen: string | null
  status_changed_at: string | null
  created_at: string
  updated_at: string
  /** Task wdwxndxexu — quando o robô notou que a empresa saiu da origem.
   *  NULL = está lá. Reaparecer limpa sozinho. */
  missing_since?: string | null
  /** Execuções completas seguidas sem ver este cliente. Vale a partir de 2. */
  missing_confirmations?: number | null
}

export interface SomaImport {
  id: string
  tenant_id: string
  import_type: SomaImportType
  file_name: string | null
  records_total: number
  records_created: number
  records_updated: number
  records_geocoded: number
  records_failed: number
  auto_healed: number
  status: SomaImportStatus
  error_message: string | null
  execution_timestamp: string | null
  started_at: string
  completed_at: string | null
  created_by: string | null
  created_at: string
}

export interface SomaImportResult {
  created: number
  updated: number
  geocoded: number
  failed: number
  autoHealed: number
  outOfArea: number
  outOfAreaClients: string[]
  errors: string[]
}

// ========================================================
// IVE 3.0 — OBSERVABILIDADE (Fase 0.4)
// ========================================================

/**
 * Linha da view credit_v2_ive_score_distribution.
 * Agrega leads por tenant / versão / classificação / dia.
 */
export interface IVEScoreDistribution {
  tenant_id: string
  ive_version: string
  ive_classificacao: string
  total_leads: number
  score_medio: number
  score_min: number
  score_max: number
  outcome_bom: number
  outcome_ruim: number
  outcome_reprovado: number
  outcome_pendente: number
  outcome_nao_registrado: number
  /** ISO date string (YYYY-MM-DD) */
  dia_analise: string
}

/**
 * Linha da view credit_v2_ive_shadow_comparison.
 * Leads onde o score shadow (IVE 3.0) foi calculado em paralelo.
 */
export interface IVEShadowComparison {
  tenant_id: string
  lead_id: string
  nome_completo: string
  cnpj: string
  ive_version: string
  score_oficial: number
  classificacao_oficial: string
  ive_score_shadow: number | null
  ive_classificacao_shadow: string | null
  ive_shadow_version: string | null
  /** score_oficial - ive_score_shadow. Positivo = oficial mais alto. */
  delta_score: number | null
  mudou_classificacao: boolean
  /** ISO 8601 */
  ive_analisado_em: string
}

/**
 * Linha da tabela credit_v2_integration_metrics.
 * Métricas de latência e custo de chamadas a integrações externas.
 */
export interface IntegrationMetric {
  id: string
  lead_id: string | null
  tenant_id: string
  /** Ex: 'gemini_analise', 'bigdatacorp_empresas', 'brasil_api' */
  integration: string
  /** Ex: 'descriptive_analysis', 'enrich_cnpj' */
  operation: string
  /** ISO 8601 */
  started_at: string
  duration_ms: number | null
  status: "ok" | "error" | "timeout" | "rate_limited"
  error_code: string | null
  /** Custo estimado em centavos de USD (4 casas decimais). */
  estimated_cost_cents: number | null
  metadata: Record<string, unknown> | null
  /** ISO 8601 */
  created_at: string
}

// ========================================================
// DOC ANALYSIS — Structured results (Wave 1 of Epic 4)
// ========================================================

export type DocAnalysisErrorType = "tecnico" | "ia" | "documento"

export interface DocAnalysisError {
  status: "error"
  source_path: string
  attempted_at: string
  error_type: DocAnalysisErrorType
  error_subtype: string
  error_message: string
  suggested_action: string
  retry_count: number
}

export interface DocAnalysisOk<T> {
  status: "ok"
  source_path: string
  analyzed_at: string
  result: T
}

export type AnaliseDocItem<T> = DocAnalysisOk<T> | DocAnalysisError

/**
 * Reads doc analysis result tolerating both legacy (flat) and new (wrapped)
 * formats. Used by TransparenciaAnalise and elsewhere in the frontend.
 */
export function unwrapDocAnalysis<T>(value: unknown): {
  status: "ok" | "error" | "absent"
  result?: T
  error?: Omit<DocAnalysisError, "status">
} {
  if (!value || typeof value !== "object") return { status: "absent" }
  const v = value as Record<string, unknown>
  if (v.status === "ok" && "result" in v) {
    return { status: "ok", result: v.result as T }
  }
  if (v.status === "error" && "error_type" in v) {
    const { status: _, ...err } = v as unknown as DocAnalysisError
    return { status: "error", error: err }
  }
  // Legacy flat shape — treat as ok
  return { status: "ok", result: value as T }
}

// ========================================================
// AUTOMATION RULES (task #15)
// ========================================================

export type AutomationTriggerType =
  | "kanban_column_change"
  | "ive_threshold"
  | "doc_uploaded"
  | "field_filled"  // task #15 v2

export type AutomationActionType =
  | "move_to_column"
  | "assign_seller"
  | "add_responsible"
  | "calculate_ive"  // task #15 v2

/** Campos do lead que podem ser usados como trigger field_filled. */
export type AutomationWatchableField =
  | "cpf"
  | "cnpj"
  | "email"
  | "celular"
  | "cidade"
  | "data_nascimento"
  | "valor_desejado"
  | "situacao_cnpj"
  | "razao_social"
  | "nome_fantasia"
  | "cnae"

/** Config do trigger varia conforme `trigger_type`. JSON schema livre. */
export interface AutomationTriggerConfig {
  /** kanban_column_change */
  column_id?: string
  /** ive_threshold */
  op?: ">=" | "<=" | "==" | "!="
  /** Aceita number (ive_threshold) ou string (field_filled mode=equals). */
  value?: number | string
  classificacao?: IVEClassification
  /** doc_uploaded */
  doc_type?: string | "*"
  /** field_filled */
  field?: AutomationWatchableField
  /** Modo do field_filled: any (qualquer valor) / empty (vazio) / equals (valor específico). */
  mode?: "any" | "empty" | "equals" | "specific" | "round_robin" | "role"
}

/** Config da ação varia conforme `action_type`. JSON schema livre. */
export interface AutomationActionConfig {
  /** move_to_column */
  // column_id reusado do trigger
  /** assign_seller / add_responsible */
  mode?: "specific" | "round_robin" | "role"
  user_id?: string
  role?: AppRole
  /** move_to_column also uses column_id */
  column_id?: string
}

export interface AutomationRule {
  id: string
  tenant_id: string
  nome: string
  descricao: string | null
  trigger_type: AutomationTriggerType
  trigger_config: AutomationTriggerConfig
  action_type: AutomationActionType
  action_config: AutomationActionConfig
  is_active: boolean
  prioridade: number
  created_by: string | null
  created_at: string
  updated_at: string
}

export interface AutomationExecution {
  id: string
  rule_id: string
  lead_id: string
  tenant_id: string
  triggered_at: string
  status: "success" | "error" | "skipped"
  error_message: string | null
  context: Record<string, unknown> | null
}

// ─── Cobrança (#164) ────────────────────────────────────────────────────────

export type CobrancaActionType = "whatsapp" | "email" | "notify_seller"

export type CobrancaActionStatus =
  | "pending"
  | "sent"
  | "failed"
  | "delivered"
  | "read"
  | "replied"

export type CobrancaTriggerSource = "manual" | "cadence"

export interface CobrancaAction {
  id: string
  tenant_id: string
  soma_client_id: string
  lead_id: string | null
  action_type: CobrancaActionType
  template_name: string | null
  triggered_by: string | null
  trigger_source: CobrancaTriggerSource
  cadence_id: string | null
  cadence_rule_index: number | null
  status: CobrancaActionStatus
  scheduled_for: string | null
  sent_at: string | null
  external_message_id: string | null
  error_message: string | null
  metadata: Record<string, unknown>
  created_at: string
}

// ========================================================
// FATURAMENTO / BILLING (mensalidade + créditos de consumo)
// Espelha as tabelas credit_v2_billing_* / credit_v2_credit_* (migrations 136-138).
// Dinheiro em centavos (int); créditos em unidades inteiras.
// ========================================================

export type BillingMeteringMode = "off" | "shadow" | "enforce"
export type TenantBillingMode = "disabled" | "shadow" | "enforce"
export type SubscriptionStatus =
  | "trialing"
  | "active"
  | "past_due"
  | "suspended"
  | "canceled"
export type CreditBucket = "included" | "purchased"
export type LedgerReason =
  | "purchase"
  | "included_grant"
  | "included_expiration"
  | "consumption"
  | "refund"
  | "manual_grant"
  | "manual_adjustment"
  | "reconciliation_refund"
  | "signup_bonus"
export type PaymentKind = "subscription" | "credit_pack"
export type PaymentStatus = "pending" | "paid" | "failed" | "refunded" | "canceled"
export type PaymentMethod = "pix" | "boleto" | "credit_card" | "manual"
/** 'manual' hoje; gateway real (asaas/stripe) na Fase 7. */
export type PaymentProvider = "manual" | "asaas" | "stripe" | (string & {})
export type ReconciliationOutcome = "refunded" | "absorbed" | "noop"

export interface BillingConfig {
  id: true
  metering_mode: BillingMeteringMode
  enforcement_blocking: boolean
  payment_provider: PaymentProvider
  credit_face_value_cents: number
  low_balance_threshold: number
  currency: string
  updated_by: string | null
  updated_at: string
}

export interface BillingPlan {
  id: string
  code: string
  name: string
  price_cents: number
  /** 0 = perfil A (sem inclusos); >0 = perfil B (inclusos expiram a cada ciclo). */
  included_credits: number
  billing_period: "monthly"
  is_active: boolean
  sort_order: number
  created_at: string
  updated_at: string
}

export interface CreditPack {
  id: string
  code: string
  name: string
  credits: number
  bonus_credits: number
  price_cents: number
  is_active: boolean
  sort_order: number
  created_at: string
  updated_at: string
}

export interface ActionCost {
  action_type: string
  label: string
  credit_cost: number
  dedup_window_hours: number
  is_billable: boolean
  description: string | null
  updated_by: string | null
  updated_at: string
}

export interface TenantSubscription {
  id: string
  tenant_id: string
  plan_id: string | null
  /** Snapshot no momento da assinatura/renovação — edição de preço só vale no próximo ciclo. */
  price_cents: number
  included_credits: number
  status: SubscriptionStatus
  billing_day: number
  current_period_start: string | null
  current_period_end: string | null
  next_due_date: string | null
  is_billing_exempt: boolean
  billing_mode: TenantBillingMode
  created_at: string
  updated_at: string
}

export interface CreditWallet {
  tenant_id: string
  /** Balde que EXPIRA a cada ciclo (inclusos do plano). */
  balance_included: number
  /** Balde que NUNCA expira (comprados/concedidos). */
  balance_purchased: number
  included_expires_at: string | null
  updated_at: string
}

export interface CreditLedgerEntry {
  id: string
  tenant_id: string
  bucket: CreditBucket
  /** Assinado: negativo = débito, positivo = crédito. */
  amount: number
  balance_after: number
  expires_at: string | null
  reason: LedgerReason
  action_type: string | null
  unit_cost_credits: number | null
  ref_type: string | null
  ref_id: string | null
  idempotency_key: string
  metadata: Record<string, unknown>
  created_by: string | null
  created_at: string
}

export interface UsageEvent {
  id: string
  tenant_id: string
  lead_id: string | null
  action_type: string
  input_digest: string | null
  billable: boolean
  would_debit_credits: number
  source: string | null
  idempotency_key: string
  created_at: string
}

export interface Payment {
  id: string
  tenant_id: string
  kind: PaymentKind
  subscription_id: string | null
  pack_id: string | null
  amount_cents: number
  currency: string
  credits_granted: number
  status: PaymentStatus
  provider: PaymentProvider
  provider_payment_id: string | null
  method: PaymentMethod | null
  snapshot: Record<string, unknown>
  period_start: string | null
  period_end: string | null
  paid_at: string | null
  idempotency_key: string | null
  created_by: string | null
  created_at: string
  updated_at: string
}

export interface ReconciliationRun {
  id: string
  tenant_id: string | null
  scope: string
  charged_credits: number
  actual_credits: number
  /** charged - actual. >0 cobrou a mais (devolve); <0 cobrou a menos (absorve). */
  delta: number
  outcome: ReconciliationOutcome
  ref_type: string | null
  ref_id: string | null
  metadata: Record<string, unknown>
  created_at: string
}

/** Saldo derivado exibido ao cliente (total + detalhe dos dois baldes). */
export interface WalletBalance {
  total: number
  included: number
  purchased: number
  included_expires_at: string | null
}
